import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { APIError, useAxios } from '../core/_axios';
import { resetPassword } from '../core/_requests';
import useLocalizedCopy from '../hooks/useLocalizedCopy';

const ResetPassword = () => {
    const { copy } = useLocalizedCopy();
    const { login } = useAuth();
    const axios = useAxios();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Redirect countdown after success (auto-login)
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                navigate('/');
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [success, navigate]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;

        // Validate passwords match
        if (password !== passwordConfirmation) {
            setError(copy.Auth.resetPassword?.passwordMismatch || 'Passwords do not match');
            return;
        }

        // Validate password length
        if (password.length < 5) {
            setError(copy.Auth.resetPassword?.passwordTooShort || 'Password must be at least 5 characters');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const response = await resetPassword(axios, token!, password, passwordConfirmation);

            // Auto-login with the returned token
            if (response.api_token) {
                login(response.api_token);
            }

            setSuccess(true);
        } catch (exception) {
            console.error(exception);
            const errorMessage = exception instanceof APIError
                ? exception.message
                : 'An error occurred. Please try again.';
            setError(errorMessage);
            setSubmitting(false);
        }
    }, [axios, token, password, passwordConfirmation, submitting, login, copy.Auth.resetPassword]);

    // Invalid or missing token
    if (!token) {
        return (
            <div className="flex items-center justify-center grow bg-center bg-no-repeat page-bg">
                <div className="card max-w-[420px] w-full">
                    <div className="card-body flex flex-col gap-5 p-10">
                        <div className="text-center">
                            <div className="flex items-center justify-center mb-5">
                                <img
                                    className="small-logo min-h-[22px] max-h-[50px] max-w-100"
                                    src="/design/web_logo_black_small.png"
                                    alt="Small Logo"
                                />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                                {copy.Auth.resetPassword?.invalidLinkTitle || 'Invalid Reset Link'}
                            </h3>
                            <p className="text-gray-600 text-sm mb-5">
                                {copy.Auth.resetPassword?.invalidLinkMessage ||
                                    'This password reset link is invalid or has expired.'}
                            </p>
                            <Link className="btn btn-primary" to="/auth/forgot-password">
                                {copy.Auth.resetPassword?.requestNewLink || 'Request a New Link'}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="flex items-center justify-center grow bg-center bg-no-repeat page-bg">
                <div className="card max-w-[420px] w-full">
                    <div className="card-body flex flex-col gap-5 p-10">
                        <div className="text-center">
                            <div className="flex items-center justify-center mb-5">
                                <img
                                    className="small-logo min-h-[22px] max-h-[50px] max-w-100"
                                    src="/design/web_logo_black_small.png"
                                    alt="Small Logo"
                                />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                                {copy.Auth.resetPassword?.successTitle || 'Password Reset Successfully'}
                            </h3>
                            <p className="text-gray-600 text-sm mb-5">
                                {copy.Auth.resetPassword?.autoLoginMessage ||
                                    'Your password has been reset. Logging you in...'}
                            </p>
                            <div className="flex justify-center">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Reset password form
    return (
        <div className="flex items-center justify-center grow bg-center bg-no-repeat page-bg">
            <div className="card max-w-[420px] w-full">
                <form className="card-body flex flex-col gap-5 p-10" onSubmit={handleSubmit}>
                    <div className="text-center mb-2.5">
                        <div className="flex items-center justify-center mb-5">
                            <img
                                className="small-logo min-h-[22px] max-h-[50px] max-w-100"
                                src="/design/web_logo_black_small.png"
                                alt="Small Logo"
                            />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                            {copy.Auth.resetPassword?.title || 'Reset Password'}
                        </h3>
                        <p className="text-gray-600 text-sm">
                            {copy.Auth.resetPassword?.subtitle || 'Enter your new password below.'}
                        </p>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="form-label text-gray-900">
                            {copy.Auth.resetPassword?.newPasswordLabel || 'New Password'}
                        </label>
                        <input
                            id="password"
                            name="password"
                            className="input"
                            placeholder={copy.Auth.resetPassword?.newPasswordPlaceholder || 'Enter new password'}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoFocus
                            minLength={5}
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="form-label text-gray-900">
                            {copy.Auth.resetPassword?.confirmPasswordLabel || 'Confirm Password'}
                        </label>
                        <input
                            id="passwordConfirmation"
                            name="passwordConfirmation"
                            className="input"
                            placeholder={copy.Auth.resetPassword?.confirmPasswordPlaceholder || 'Confirm new password'}
                            type="password"
                            value={passwordConfirmation}
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            required
                            minLength={5}
                        />
                    </div>

                    <button
                        className="btn btn-primary flex justify-center grow"
                        type="submit"
                        disabled={submitting}
                    >
                        {!submitting
                            ? (copy.Auth.resetPassword?.resetButton || 'Reset Password')
                            : (copy.Auth.resetPassword?.resetting || 'Resetting...')
                        }
                    </button>

                    {error && (
                        <div className="text-center p-4 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                            {error}
                        </div>
                    )}

                    <div className="text-center">
                        <Link className="text-2sm link" to="/auth/login">
                            {copy.Auth.forgotPassword?.backToLogin || 'Back to Login'}
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
